import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TooltipProvider } from '@roger/ui-components';
import type { AttachmentOut, MessageOut } from '../../services/api';
import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import type { PendingSend } from '../../hooks/chat/useChatMutations';
import { MOTION } from './chatMotion';
import { dayDividerLabel } from './dayDivider';
import { MessageBubble } from './MessageBubble';
import { MessageText } from './MessageText';

interface MessageListProps {
  messages: MessageOut[];
  pending: PendingSend[];
  currentUserId: string;
  /** Sender names show only in group chats. */
  isGroup: boolean;
  lastReadSeq: number;
  /** False for a non-member: the read cursor does not move, so no New line. */
  showNewDivider?: boolean;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  onRetry: (clientId: string, body: string) => void;
  urls?: Record<string, AttachmentUrl | null | undefined>;
  onOpenAttachment?: (tiles: AttachmentOut[], index: number) => void;
  onDownloadAttachment?: (id: string) => void;
  /** Where the list was left, off the bottom; used instead of the bottom. */
  initialScrollTop?: number;
  /** The newest seq when that position was saved. */
  initialNewestSeq?: number;
  /** `top` is null at the bottom, so the next mount follows the bottom. */
  onScrollTopChange?: (top: number | null, newestSeq: number) => void;
  /** Phone sheet: a tap on a bubble selects it and shows who reacted. */
  touch?: boolean;
  canReact?: boolean;
  onToggleReaction?: (
    messageId: string,
    emoji: string,
    reacted: boolean
  ) => void;
  /** Messages with a reaction write in flight. */
  pendingReactionIds?: ReadonlySet<string>;
  /** True when the channel takes writes: own messages get the options menu. */
  canChange?: boolean;
  onEditMessage?: (messageId: string, body: string) => Promise<unknown>;
  onDeleteMessage?: (messageId: string) => Promise<unknown>;
  /**
   * The channel's history has settled (`messagesQuery.isSuccess &&
   * !messagesQuery.isFetching`): the same signal the live announcement uses.
   * A row that appears after this first turns true is a live arrival.
   */
  historyReady: boolean;
}

/** A row's offset from the top of the list's content, whatever the scroll. */
const contentTop = (list: HTMLElement, row: HTMLElement): number =>
  row.getBoundingClientRect().top -
  list.getBoundingClientRect().top +
  list.scrollTop;

/** The one bottom-geometry write: pin a scroll box to its content height. */
const bottomScroll = (el: HTMLElement | null) => {
  if (el) el.scrollTop = el.scrollHeight;
};

export function MessageList({
  messages,
  pending,
  currentUserId,
  isGroup,
  lastReadSeq,
  showNewDivider = true,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
  onRetry,
  urls,
  onOpenAttachment,
  onDownloadAttachment,
  initialScrollTop,
  initialNewestSeq,
  onScrollTopChange,
  touch = false,
  canReact = false,
  onToggleReaction,
  pendingReactionIds,
  canChange = false,
  onEditMessage,
  onDeleteMessage,
  historyReady,
}: MessageListProps) {
  const ref = useRef<HTMLDivElement>(null);
  // The rows, so a size observer sees content growth that is not an arrival.
  const contentRef = useRef<HTMLDivElement>(null);
  // The New messages pill, so an arrival that hides it can hand focus on.
  const pillRef = useRef<HTMLButtonElement>(null);
  const atBottomRef = useRef(true);
  // A restored position waits for rows: the list can mount while history loads.
  const restoreRef = useRef(initialScrollTop);
  // Restoring is not an arrival: the bottom-follow pass for this exact state
  // skips. Keyed on a value, so a StrictMode double run skips both times.
  const skipFollowRef = useRef<string | null>(null);
  const newestSeq = messages[messages.length - 1]?.seq ?? 0;
  const newestSeqRef = useRef(0);
  // The newest seq at the moment history first settled (0 for an empty
  // channel). Latched once, after commit, so a discarded render cannot publish
  // it: everything at or below it is history. A stale cache and the mount
  // refetch that replaces it both land here, so neither animates.
  const liveAfterSeqRef = useRef<number | null>(null);
  // Every client id this list has shown as a pending row, mount included. A
  // message from one of them settles instead of entering: the send that made
  // the pending row reached the server before the row it becomes.
  const everPendingRef = useRef<Set<string>>(new Set());
  const pendingIds = new Set(pending.map(row => row.client_id));
  // Publish these baselines after commit, never during render: a render React
  // discards must not change what a later committed render reads. The render
  // still sees this commit's pending ids through `pendingIds`.
  useEffect(() => {
    newestSeqRef.current = newestSeq;
    for (const clientId of pendingIds) everPendingRef.current.add(clientId);
    if (historyReady && liveAfterSeqRef.current === null)
      liveAfterSeqRef.current = newestSeq;
  });
  const onScrollTopChangeRef = useRef(onScrollTopChange);
  onScrollTopChangeRef.current = onScrollTopChange;
  // The top row when the older page was asked for, and its offset in the
  // list's content. Once that page renders, scrollTop moves by how far that row
  // moved, so the reader stays on it. A live arrival at the bottom does not
  // move the row, so it does not shift the view.
  const olderAnchorRef = useRef<{ seq: string; top: number } | null>(null);
  const failedRetryRef = useRef<HTMLButtonElement>(null);
  const [showNewPill, setShowNewPill] = useState(false);
  // The selected message on the phone, by seq. A tap outside any bubble clears it.
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const today = new Date();
  // A reply means the person read what came before it, so the New line only
  // marks messages from others after their last message, and goes as soon as
  // a reply is on its way.
  const lastOwnSeq = messages.reduce(
    (seq, m) => (m.sender_id === currentUserId ? Math.max(seq, m.seq) : seq),
    0
  );
  const firstUnreadSeq =
    pending.length > 0 || !showNewDivider
      ? undefined
      : messages.find(
          m =>
            m.seq > Math.max(lastReadSeq, lastOwnSeq) &&
            m.sender_id !== currentUserId
        )?.seq;

  const followKey = `${messages[messages.length - 1]?.seq}:${pending.length}`;
  // The send already merged these server rows, and each store notifies on its
  // own timer, so both can render in one commit. A settle fade from 60% would
  // paint the text twice, so the pending row that has become a message hides.
  // `followKey` and `firstUnreadSeq` still read the raw `pending` prop: the
  // send is not finished until the mutation itself settles.
  const clientIdsInMessages = new Set(messages.map(m => m.client_id));

  // Explicit activation and a genuine arrival follow the bottom and hide the
  // pill. A hidden, focused button would hold focus outside the dialog, so a
  // pill that owns focus hands it to the list first.
  const scrollToBottom = () => {
    if (document.activeElement === pillRef.current)
      ref.current?.focus({ preventScroll: true });
    bottomScroll(ref.current);
    setShowNewPill(false);
  };

  // The pill hides while it has focus, so the click has to move focus into the
  // list: a hidden, focused button stays in the accessibility tree. The list is
  // focusable for this (`tabIndex={-1}`), so focus stays in the chat surface
  // instead of falling to the page.
  const dismissPill = () => {
    scrollToBottom();
    ref.current?.focus({ preventScroll: true });
  };

  // Before paint, so a restored list never flashes at the top.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || restoreRef.current === undefined || messages.length === 0)
      return;
    el.scrollTop = restoreRef.current;
    restoreRef.current = undefined;
    skipFollowRef.current = followKey;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    // Arrivals while minimized sit below the restored position.
    if (newestSeq > (initialNewestSeq ?? 0)) setShowNewPill(true);
  }, [messages.length]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      atBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      onScrollTopChangeRef.current?.(
        atBottomRef.current ? null : el.scrollTop,
        newestSeqRef.current
      );
      if (el.scrollTop === 0 && hasOlder && !isLoadingOlder) {
        const row = el.querySelector<HTMLElement>('[data-seq]');
        olderAnchorRef.current = row
          ? { seq: row.dataset.seq ?? '', top: contentTop(el, row) }
          : null;
        onLoadOlder();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasOlder, isLoadingOlder, onLoadOlder]);

  // Before paint, so the view never shows the older rows at the top first.
  // A failed or empty load does not move the row and only clears the anchor.
  useLayoutEffect(() => {
    const el = ref.current;
    const anchor = olderAnchorRef.current;
    if (!el || anchor === null || isLoadingOlder) return;
    olderAnchorRef.current = null;
    const row = el.querySelector<HTMLElement>(`[data-seq="${anchor.seq}"]`);
    if (row) el.scrollTop += contentTop(el, row) - anchor.top;
  }, [messages[0]?.seq, isLoadingOlder]);

  // The list is oldest first, so a channel would open on the oldest page and an
  // arrival would land out of view. Follow the bottom, or offer the pill.
  useEffect(() => {
    if (skipFollowRef.current === followKey) return;
    skipFollowRef.current = null;
    if (atBottomRef.current) scrollToBottom();
    else setShowNewPill(true);
  }, [messages[messages.length - 1]?.seq, pending.length]);

  // Content growth is not an arrival: an edit, a reaction, the inline editor
  // opening or a pending row's status changing makes a row taller without
  // changing the newest seq, so the follow pass above never runs. Watch the
  // rows' size instead: at the bottom, keep the newest row in view; off the
  // bottom, leave the reader where they are and never offer the pill.
  useEffect(() => {
    const el = ref.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) bottomScroll(el);
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // A pending row that turns failed keeps its Retry line in view.
  const failedCount = pending.filter(p => p.status === 'failed').length;
  useEffect(() => {
    if (failedCount > 0)
      failedRetryRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [failedCount]);

  let lastDay = '';
  const lastFailedId = pending
    .filter(p => p.status === 'failed')
    .pop()?.client_id;
  const list = (
    <div className='relative flex min-h-0 flex-1 flex-col'>
      <div
        ref={ref}
        data-testid='chat-message-list'
        // A log that stays silent: ConversationBody announces new messages.
        role='log'
        aria-live='off'
        // The pill hands focus here when it hides; -1 keeps it out of Tab.
        tabIndex={-1}
        // 4 px block-end padding: the entering row's 4 px slide stays inside
        // the scrollable overflow, so the scroll height does not change while
        // the list follows the bottom (spec "Scroll at the bottom").
        className='flex-1 overflow-y-auto pb-1 outline-none'
        onClick={event => {
          // React events from a portaled picker bubble up this tree.
          const target = event.target as Element;
          if (
            ref.current?.contains(target) &&
            !target.closest('[data-chat-bubble]')
          )
            setSelectedSeq(null);
        }}
      >
        <div ref={contentRef}>
          {messages.map((message, index) => {
            const label = dayDividerLabel(new Date(message.created_at), today);
            const divider = label === lastDay ? null : (lastDay = label);
            // Settle beats live: a message this list showed as a pending row is
            // one of ours on its way in, whatever its seq. The committed set
            // covers earlier commits; this render's own `pendingIds` cover a
            // first snapshot where the server row and its pending row arrive
            // together, before the commit effect has recorded the id.
            const arrival =
              everPendingRef.current.has(message.client_id) ||
              pendingIds.has(message.client_id)
                ? 'sent'
                : liveAfterSeqRef.current !== null &&
                    message.seq > liveAfterSeqRef.current
                  ? 'live'
                  : undefined;
            return (
              <React.Fragment key={message.seq}>
                {divider && (
                  <div className='px-3 py-1 text-center text-[11px] text-muted-foreground'>
                    {divider}
                  </div>
                )}
                {message.seq === firstUnreadSeq && (
                  <div className='px-3 py-1 text-[11px] font-medium text-primary'>
                    New
                  </div>
                )}
                <MessageBubble
                  message={message}
                  isOwn={message.sender_id === currentUserId}
                  showSender={
                    isGroup &&
                    message.sender_id !== currentUserId &&
                    messages[index - 1]?.sender_id !== message.sender_id
                  }
                  urls={urls}
                  onOpenAttachment={onOpenAttachment}
                  onDownloadAttachment={onDownloadAttachment}
                  currentUserId={currentUserId}
                  touch={touch}
                  canReact={canReact}
                  arrival={arrival}
                  historyReady={historyReady}
                  selected={touch && selectedSeq === message.seq}
                  onSelect={() => setSelectedSeq(message.seq)}
                  pendingReaction={pendingReactionIds?.has(message.id)}
                  onToggleReaction={(emoji, reacted) =>
                    onToggleReaction?.(message.id, emoji, reacted)
                  }
                  canChange={canChange}
                  onEdit={
                    onEditMessage
                      ? body => onEditMessage(message.id, body)
                      : undefined
                  }
                  onDelete={
                    onDeleteMessage
                      ? () => onDeleteMessage(message.id)
                      : undefined
                  }
                />
              </React.Fragment>
            );
          })}

          {pending
            .filter(p => !clientIdsInMessages.has(p.client_id))
            .map(p => (
              <div
                key={p.client_id}
                data-pending
                className={`flex flex-col items-end px-3 py-0.5 ${
                  p.status === 'pending' ? MOTION.enter : ''
                }`}
              >
                <div className='max-w-[86%] rounded-[12px_12px_4px_12px] bg-primary-600 px-3 py-2 text-sm leading-5 text-primary-foreground opacity-60'>
                  <p className='whitespace-pre-wrap [overflow-wrap:anywhere]'>
                    <MessageText
                      body={p.body}
                      currentUserId={currentUserId}
                      isOwn
                    />
                  </p>
                  {p.attachment_ids.length > 0 && (
                    <p className='text-[11px]'>
                      {p.attachment_ids.length === 1
                        ? '1 file'
                        : `${p.attachment_ids.length} files`}
                    </p>
                  )}
                </div>
                {p.status === 'failed' && (
                  <button
                    ref={
                      p.client_id === lastFailedId ? failedRetryRef : undefined
                    }
                    type='button'
                    className='mt-0.5 text-[11px] text-destructive'
                    onClick={() => onRetry(p.client_id, p.body)}
                  >
                    Not sent · Retry
                  </button>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Always mounted, so it can fade both ways. `animate-in` would set
          `transform` and undo the `-translate-x-1/2` centering, so the hide and
          show are an opacity transition. Hidden, it takes no pointer, keyboard
          or screen reader focus. */}
      <button
        ref={pillRef}
        type='button'
        onClick={dismissPill}
        aria-hidden={!showNewPill}
        // Disabled while hidden: it is then out of the tab order, out of
        // `ChatSheet`'s `button:not([disabled])` focus trap, and unable to
        // take script focus. It is still mounted, so it can fade.
        disabled={!showNewPill}
        tabIndex={showNewPill ? 0 : -1}
        className={`absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs text-white motion-safe:transition-opacity motion-safe:duration-150 ${
          showNewPill ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        New messages ↓
      </button>
    </div>
  );
  return <TooltipProvider>{list}</TooltipProvider>;
}
